import React from 'react'

import { FinancialsTableContextProvider } from '../../components/financials-table'
import { useFinancialsTablePersistScrollPosition } from '../../components/financials-table/useFinancialsTablePersistScrollPosition'
import { useCachedLoaderData } from '../../hooks/useCachedLoaderData'
import { QuoteFinancialsTabMenu } from '../quote/components/QuoteFinancialsTabMenu'
import { QuoteStickySubMenu } from '../quote/components/QuoteStickySubMenu'
import CategoryWidget from './CategoryWidget'
import WidgetSettingsSection from './WidgetSettingsSection'
import { COOKIE_KEY, DEFAULT_SETTINGS } from './constants'
import { RevenueData, RevenueDataEnum } from './types'

function getAreasCounts(data: RevenueData | null) {
  if (!data) return []

  return Object.keys(data).map((key) => {
    const category = data[key as keyof RevenueData]
    return Object.keys(category.revenues).length
  })
}

function RevenueComponent() {
  const { data } = useCachedLoaderData<RevenueData | null>()
  const categoryAreasCounts = React.useMemo(() => getAreasCounts(data), [data])

  useFinancialsTablePersistScrollPosition({
    categoryAreasCounts,
    dataKeys: [RevenueDataEnum.yoyChange, RevenueDataEnum.yoyChangePct, RevenueDataEnum.sharePct],
  })

  React.useEffect(() => {
    // Remove min-height from react root element which is set on BE because of CLS
    document.getElementById('root')?.style.removeProperty('min-height')
  }, [])

  const hasData = !!data && Object.keys(data).length > 0

  return (
    <>
      <QuoteStickySubMenu
        leftContent={<QuoteFinancialsTabMenu />}
        rightContent={hasData ? <WidgetSettingsSection /> : undefined}
      />

      {hasData ? (
        <div className="fv-container mb-4 mt-2 space-y-10">
          {Object.entries(data).map(([key, val]) => (
            <CategoryWidget key={key} category={key} data={val} />
          ))}
        </div>
      ) : (
        <div className="fv-container flex h-60 items-center justify-center text-muted">
          Revenue is not available for this stock.
        </div>
      )}
    </>
  )
}

export default function Revenue() {
  return (
    <FinancialsTableContextProvider defaultSettings={DEFAULT_SETTINGS} cookieKey={COOKIE_KEY}>
      <RevenueComponent />
    </FinancialsTableContextProvider>
  )
}
